import { Components, Theme } from '@mui/material/styles'

export const components: Components<Omit<Theme, 'components'>> = {
  // Button customizations
  MuiButton: {
    styleOverrides: {
      root: {
        borderRadius: 4,
        textTransform: 'none',
        fontWeight: 500,
        boxShadow: 'none',
        '&:hover': {
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
        },
      },
      contained: {
        '&:hover': {
          boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.15)',
        },
      },
    },
  },

  // Card customizations
  MuiCard: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
        '&:hover': {
          boxShadow: '0px 3px 6px rgba(0, 0, 0, 0.16), 0px 3px 6px rgba(0, 0, 0, 0.23)',
        },
      },
    },
  },

  // Paper customizations
  MuiPaper: {
    styleOverrides: {
      root: {
        borderRadius: 8,
      },
      elevation1: {
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12), 0px 1px 2px rgba(0, 0, 0, 0.24)',
      },
    },
  },

  // Table customizations for data density
  MuiTableHead: {
    styleOverrides: {
      root: {
        backgroundColor: '#f5f5f5',
      },
    },
  },

  MuiTableCell: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid rgba(224, 224, 224, 1)',
        padding: '12px 16px',
      },
      head: {
        backgroundColor: '#f5f5f5',
        fontWeight: 600,
        fontSize: '0.875rem',
        color: 'rgba(0, 0, 0, 0.87)',
      },
      body: {
        fontSize: '0.875rem',
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
    },
  },

  MuiTableRow: {
    styleOverrides: {
      root: {
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          cursor: 'pointer',
        },
        '&.Mui-selected': {
          backgroundColor: 'rgba(25, 118, 210, 0.08)',
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.12)',
          },
        },
      },
    },
  },

  // Chip customizations
  MuiChip: {
    styleOverrides: {
      root: {
        borderRadius: 16,
        fontSize: '0.75rem',
        height: 24,
      },
      colorPrimary: {
        backgroundColor: '#e3f2fd',
        color: '#1565c0',
      },
      colorSecondary: {
        backgroundColor: '#f3e5f5',
        color: '#7b1fa2',
      },
    },
  },

  // Drawer customizations
  MuiDrawer: {
    styleOverrides: {
      paper: {
        borderRight: '1px solid rgba(0, 0, 0, 0.12)',
        backgroundColor: '#fafafa',
      },
    },
  },

  // AppBar customizations
  MuiAppBar: {
    styleOverrides: {
      root: {
        backgroundColor: '#ffffff',
        color: 'rgba(0, 0, 0, 0.87)',
        boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.12)',
      },
    },
  },

  // Toolbar customizations
  MuiToolbar: {
    styleOverrides: {
      root: {
        minHeight: '64px !important',
        paddingLeft: '24px !important',
        paddingRight: '24px !important',
      },
    },
  },

  // TextField customizations
  MuiTextField: {
    styleOverrides: {
      root: {
        '& .MuiOutlinedInput-root': {
          borderRadius: 4,
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
      },
    },
  },

  // Select customizations
  MuiSelect: {
    styleOverrides: {
      root: {
        borderRadius: 4,
      },
    },
  },

  // Menu customizations
  MuiMenu: {
    styleOverrides: {
      paper: {
        borderRadius: 8,
        boxShadow: '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
      },
    },
  },

  // Dialog customizations
  MuiDialog: {
    styleOverrides: {
      paper: {
        borderRadius: 12,
      },
    },
  },

  // Tabs customizations
  MuiTabs: {
    styleOverrides: {
      root: {
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
      },
      indicator: {
        height: 3,
        borderRadius: '3px 3px 0 0',
      },
    },
  },

  MuiTab: {
    styleOverrides: {
      root: {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.875rem',
        minHeight: 48,
        '&.Mui-selected': {
          color: '#1565c0',
        },
      },
    },
  },

  // IconButton customizations
  MuiIconButton: {
    styleOverrides: {
      root: {
        borderRadius: 8,
        '&:hover': {
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
        },
      },
    },
  },

  // Tooltip customizations
  MuiTooltip: {
    defaultProps: {
      arrow: true,
      enterDelay: 200,
      leaveDelay: 0,
    },
    styleOverrides: {
      tooltip: {
        backgroundColor: 'rgba(50, 50, 50, 0.95)',
        fontSize: '0.75rem',
        borderRadius: 4,
        padding: '6px 10px',
      },
      arrow: {
        color: 'rgba(50, 50, 50, 0.95)',
      },
    },
  },

  // Custom typography variants
  MuiTypography: {
    variants: [
      {
        props: { variant: 'tableHeader' },
        style: {
          fontSize: '0.875rem',
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.87)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
      {
        props: { variant: 'tableCell' },
        style: {
          fontSize: '0.875rem',
          fontWeight: 400,
          color: 'rgba(0, 0, 0, 0.87)',
        },
      },
      {
        props: { variant: 'cardTitle' },
        style: {
          fontSize: '1.125rem',
          fontWeight: 500,
          color: 'rgba(0, 0, 0, 0.87)',
          marginBottom: '8px',
        },
      },
      {
        props: { variant: 'metricValue' },
        style: {
          fontSize: '1.5rem',
          fontWeight: 600,
          color: 'rgba(0, 0, 0, 0.87)',
          lineHeight: 1.2,
        },
      },
      {
        props: { variant: 'metricLabel' },
        style: {
          fontSize: '0.75rem',
          fontWeight: 500,
          color: 'rgba(0, 0, 0, 0.6)',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
        },
      },
    ],
  },
}